/* InputControls.js */

"use strict";

const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const RendererUtils = require("./RendererUtils");
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const InputCheckControl = require("@gluenetworks/steve_ui_core").Control.Boolean.InputCheckbox;
const InputNumberControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputNumber;
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single.ComboBox;

class InstancesOfTypesHandler {
    constructor(workTriggerInstanceEditor, construct, typeCombo, instanceCombo) {
        this._workTriggerInstanceEditor = workTriggerInstanceEditor;
        this._construct = construct;
        this._typeCombo = typeCombo;
        this._instanceCombo = instanceCombo;
    }

    registerListeners() {
        this._typeCombo.on("UserValue", (this._onTypeChange = this._onTypeChange.bind(this)));
    }

    unregisterListeners() {
        this._typeCombo.removeListener("UserValue", this._onTypeChange);
    }

    setType(type) {
        return this._workTriggerInstanceEditor.getConstructInstances(this._construct, type).then((instances) => {
            let options = instances.map((instance) => {
                return { label: instance.name, value: instance.id };
            });
            this._instanceCombo.options = options;
            if (options[0]) {
                this._instanceCombo.setValue(options[0].value);
            } else {
                this._instanceCombo.setValue();
            }
        });
    }

    _onTypeChange(newVal, oldVal) {
        if (newVal !== oldVal) {
            this.setType(newVal);
        }
    }
}

class AssociatedNodesHandler {
    constructor(workTriggerInstanceEditor, constructCombo, typeCombo, instanceCombo) {
        this._workTriggerInstanceEditor = workTriggerInstanceEditor;
        this._constructCombo = constructCombo;
        this._typeCombo = typeCombo;
        this._instanceCombo = instanceCombo;
    }

    registerListeners() {
        this._constructCombo.on("UserValue", (this._onConstructChange = this._onConstructChange.bind(this)));
        this._typeCombo.on("UserValue", (this._onTypeChange = this._onTypeChange.bind(this)));
    }

    unregisterListeners() {
        this._constructCombo.removeListener("UserValue", this._onConstructChange);
        this._typeCombo.removeListener("UserValue", this._onTypeChange);
    }

    setContruct(construct) {
        return this._workTriggerInstanceEditor.getConstructTypes(construct).then((types) => {
            let options = types.map((type) => {
                return { label: type.displayName, value: type.id };
            });
            this._typeCombo.options = options;
            if (options[0]) {
                this._typeCombo.setValue(options[0].value);
                this.setType(options[0].value);
            } else {
                this._typeCombo.setValue();
                this._instanceCombo.setValue();
            }
        });
    }

    setType(type) {
        return this._workTriggerInstanceEditor.getConstructInstances(this._constructCombo.value, type).then((instances) => {
            let options = instances.map((instance) => {
                return { label: instance.name, value: instance.id };
            });
            this._instanceCombo.options = options;
            if (options[0]) {
                this._instanceCombo.setValue(options[0].value);
            } else {
                this._instanceCombo.setValue();
            }
        });
    }

    _onConstructChange(newVal, oldVal) {
        if (newVal !== oldVal) {
            this.setContruct(newVal);
        }
    }
    _onTypeChange(newVal, oldVal) {
        if (newVal !== oldVal) {
            this.setType(newVal);
        }
    }
}

class InputControls extends EventEmitter {
    constructor(workTriggerInstanceEditor, inputsDef, gridContainerElem, startTabIndex) {
        super();
        this._workTriggerInstanceEditor = workTriggerInstanceEditor;
        this._inputsDef = inputsDef;
        this._gridContainerElem = gridContainerElem;
        this._startTabIndex = startTabIndex || 0;
        this._controls = {};
        this._parts = [];
        this._handlers = [];
        this._constantants = {};
    }

    render(beforeElem) {
        let loadPromiseChain = Promise.resolve();
        if (this._inputsDef && _.isArray(this._inputsDef)) {
            this._inputsDef.forEach((workInput) => {
                let loadPromise;
                switch (workInput.type) {
                    case "constant":
                        this._constantants[workInput.name] = workInput.value;
                        break;
                    case "text":
                        loadPromise = Promise.resolve(new InputTextControl());
                        break;
                    case "bool":
                        loadPromise = Promise.resolve(new InputCheckControl());
                        break;
                    case "number":
                        loadPromise = Promise.resolve(new InputNumberControl());
                        break;
                    case "enum":
                        let enumCombo = new ComboBoxControl({
                            options: workInput.options,
                            showFilter: false
                        });
                        if (workInput.options[0]) {
                            enumCombo.setValue(workInput.options[0].value, true);
                        }
                        loadPromise = Promise.resolve(enumCombo);
                        break;
                    case "constructs":
                        let constructOptions = [
                            { value: "domain", label: "Domain" },
                            { value: "feature", label: "Feature" },
                            { value: "global", label: "Global" },
                            { value: "node", label: "Node" },
                            { value: "script", label: "Script" }
                        ];
                        let constructsCombo = new ComboBoxControl({
                            options: constructOptions,
                            showFilter: false
                        });
                        constructsCombo.setValue(constructOptions[0].value, true);
                        loadPromise = Promise.resolve(constructsCombo);
                        break;
                    case "controlUsers":
                        loadPromise = this._workTriggerInstanceEditor.getControlUsers().then((users) => {
                            let options = users.map((user) => {
                                return {
                                    label: `${user.firstName || "Unknown first name"} ${user.lastName || "Unknown last name"} (${user.userName})`,
                                    value: user.id
                                };
                            });
                            let usersCombo = new ComboBoxControl({
                                options: options,
                                showFilter: true
                            });
                            if (options[0]) {
                                usersCombo.setValue(options[0].value, true);
                            }
                            return usersCombo;
                        });
                        break;
                    case "constructTypes":
                        loadPromise = this._workTriggerInstanceEditor.getConstructTypes(workInput.construct).then((types) => {
                            let options = types.map((type) => {
                                return { label: type.displayName, value: type.id };
                            });
                            let typesCombo = new ComboBoxControl({
                                options: options,
                                showFilter: true
                            });
                            if (options[0]) {
                                typesCombo.setValue(options[0].value, true);
                            }
                            return typesCombo;
                        });
                        break;
                    case "constructInstances":
                        loadPromise = this._workTriggerInstanceEditor
                            .getConstructInstancesForTypeName(workInput.construct, workInput.typeName)
                            .then((instances) => {
                                let options = instances.map((instance) => {
                                    return { label: instance.name, value: instance.id };
                                });
                                let instancesCombo = new ComboBoxControl({
                                    options: options,
                                    showFilter: true
                                });
                                if (options[0]) {
                                    instancesCombo.setValue(options[0].value, true);
                                }
                                return instancesCombo;
                            });
                        break;
                    case "instancesOfTypes":
                        loadPromise = this._workTriggerInstanceEditor.getConstructTypes(workInput.construct).then((types) => {
                            let options = types.map((type) => {
                                return { label: type.displayName, value: type.id };
                            });
                            let typesCombo = new ComboBoxControl({
                                options: options,
                                showFilter: true
                            });
                            let instancesCombo = new ComboBoxControl({
                                options: [],
                                showFilter: true
                            });
                            let handler = new InstancesOfTypesHandler(
                                this._workTriggerInstanceEditor,
                                workInput.construct,
                                typesCombo,
                                instancesCombo
                            );
                            handler.registerListeners();
                            this._handlers.push(handler);
                            if (options[0]) {
                                typesCombo.setValue(options[0].value, true);
                                handler.setType(options[0].value);
                            }
                            return [typesCombo, instancesCombo];
                        });
                        break;
                    case "associatedNodes":
                        loadPromise = Promise.resolve().then(() => {
                            let associatedConstructOptions = [
                                { value: "domain", label: "Domain" },
                                { value: "feature", label: "Feature" },
                                { value: "global", label: "Global" }
                            ];
                            let associatedConstructCombo = new ComboBoxControl({
                                options: associatedConstructOptions,
                                showFilter: false
                            });
                            associatedConstructCombo.setValue("domain", true);
                            let typesCombo = new ComboBoxControl({
                                options: [],
                                showFilter: false
                            });
                            let instancesCombo = new ComboBoxControl({
                                options: [],
                                showFilter: false
                            });
                            let handler = new AssociatedNodesHandler(
                                this._workTriggerInstanceEditor,
                                associatedConstructCombo,
                                typesCombo,
                                instancesCombo
                            );
                            handler.registerListeners();
                            this._handlers.push(handler);
                            return handler.setContruct("domain").then(() => {
                                return [associatedConstructCombo, typesCombo, instancesCombo];
                            });
                        });
                        break;
                }
                if (loadPromise) {
                    loadPromiseChain = loadPromiseChain
                        .then(() => {
                            return loadPromise;
                        })
                        .then((control) => {
                            let multiControl = _.isArray(control);
                            if (multiControl) {
                                control.forEach((c, idx) => {
                                    let name = workInput.names && workInput.names[idx] ? workInput.names[idx] : `${workInput.name}[${idx}]`;
                                    this._controls[name] = c;
                                    c.on("UserValue", (this._onValueChange = this._onValueChange.bind(this)));
                                });
                            } else {
                                this._controls[workInput.name] = control;
                                control.on("UserValue", (this._onValueChange = this._onValueChange.bind(this)));
                            }
                            let parts = RendererUtils.groupGridRowElementsBefore(
                                beforeElem,
                                this._startTabIndex++,
                                this._gridContainerElem,
                                `work-input-${workInput.name}`,
                                workInput.label,
                                control
                            );
                            if (workInput.class) {
                                _.last(parts).classList.add(workInput.class);
                            }
                            this._parts = this._parts.concat(parts);
                        });
                }
            });
        }
        return loadPromiseChain
            .then(() => {
                let renderPromises = _.values(this._controls).map((c) => {
                    return c.renderer.render();
                });
                return Promise.all(renderPromises);
            })
            .then(() => {
                return this._parts;
            });
    }

    getValues() {
        let values = {};
        _.forEach(this._controls, (control, name) => {
            _.set(values, name, control.getValue());
        });
        _.forEach(this._constantants, (value, name) => {
            _.set(values, name, value);
        });
        return values;
    }

    setValues(values) {
        _.forEach(this._controls, (control, name) => {
            let val = _.get(values, name);
            if (!_.isUndefined(val)) {
                control.setValue(val);
            }
        });
    }

    shutdown() {
        RendererUtils.removeChildElements(this._gridContainerElem, this._parts);
        return Promise.all(
            _.values(this._controls).map((component) => {
                return component.shutdown();
            })
        ).then(() => {
            _.values(this._controls).forEach((control) => {
                control.removeListener("UserValue", this._onValueChange);
            });
            this._handlers.forEach((handler) => {
                handler.unregisterListeners();
            });
            this._workTriggerInstanceEditor = undefined;
            this._inputsDef = undefined;
            this._gridContainerElem = undefined;
            this._controls = undefined;
            this._parts = undefined;
            this._handlers = undefined;
            this._constantants = undefined;
        });
    }

    getFirstElem() {
        return this._parts[0];
    }

    _onValueChange() {
        this.emit("ValueChange", this.getValues());
    }
}

module.exports = InputControls;
